export type Tag = 'new' | 'fix' | 'imp';

export interface ReleaseFile {
  version: string;
  date: string;
  items?: { tag: Tag; text: string }[];
}

export interface Entry {
  v: string;
  d: string;
  items: [Tag, string][];
}

const VERSION = /^v(\d+)\.(\d+)\.(\d+)$/;

const sortKey = (version: string): number => {
  const m = VERSION.exec(version);
  return m ? Number(m[1]) * 1e8 + Number(m[2]) * 1e4 + Number(m[3]) : -1;
};

/**
 * The page's entries, one per minor (`v4.17`), from the release files the API ships its What's new
 * log from (apps/panel-api/src/release-notes/data). A minor's items are those of all its releases,
 * newest release first, and its date is the newest release's. A release with no items (a silent
 * patch) adds nothing. Anchors stay `#v4.17`, as before.
 */
export function buildEntries(files: ReleaseFile[]): Entry[] {
  const releases = files
    .filter((f) => VERSION.test(f.version) && f.items && f.items.length > 0)
    .sort((a, b) => sortKey(b.version) - sortKey(a.version));

  const byMinor = new Map<string, Entry>();
  for (const release of releases) {
    const [, major, minor] = VERSION.exec(release.version) as RegExpExecArray;
    const key = `v${major}.${minor}`;
    const entry = byMinor.get(key) ?? { v: key, d: release.date, items: [] };
    entry.items.push(...(release.items ?? []).map((i): [Tag, string] => [i.tag, i.text]));
    byMinor.set(key, entry);
  }
  return [...byMinor.values()];
}
